import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "react-alert";
import { changeUserPassword, clearErrors } from "../../../../actions/adminUserActions";
import { CHANGE_PASSWORD_RESET } from "../../../../constants/userConstants";

const ChangePasswordModal = ({ show, onClose, userId, userEmail }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const alert = useAlert();

  const { passwordChanged, passwordChangeLoading, passwordChangeError } = useSelector(
    (state) => state.users
  );

  useEffect(() => {
    if (passwordChangeError) {
      alert.error(passwordChangeError);
      dispatch(clearErrors());
    }

    if (passwordChanged) {
      alert.success("Password changed successfully");
      setNewPassword("");
      setConfirmPassword("");
      onClose();
      dispatch({ type: CHANGE_PASSWORD_RESET });
    }
  }, [dispatch, alert, passwordChanged, passwordChangeError, onClose]);

  const validateForm = () => {
    const newErrors = {};

    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm the password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    dispatch(changeUserPassword(userId, newPassword, sendEmail));
  };

  const handleClose = () => {
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
    onClose();
  };

  if (!show) {
    return null;
  }

  const modalContent = (
    <div>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9998,
        }}
      />
      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          zIndex: 9999,
          width: "450px",
          maxWidth: "90%",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "15px 20px",
            borderBottom: "1px solid #e5e5e5",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h4 style={{ margin: 0, fontSize: "18px" }}>
            <i className="fa fa-key" style={{ marginRight: "8px" }}></i>
            Change Password
          </h4>
          <button
            type="button"
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              padding: "0",
              lineHeight: "1",
            }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: "20px" }}>
            <p style={{ color: "#666", marginBottom: "15px" }}>
              Changing password for: <strong>{userEmail}</strong>
            </p>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                New Password
              </label>
              <input
                type="password"
                className="form-control"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
              {errors.newPassword && (
                <span style={{ color: "red", fontSize: "12px" }}>{errors.newPassword}</span>
              )}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Confirm Password
              </label>
              <input
                type="password"
                className="form-control"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
              {errors.confirmPassword && (
                <span style={{ color: "red", fontSize: "12px" }}>{errors.confirmPassword}</span>
              )}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  style={{ marginRight: "8px" }}
                />
                Send password to user via email
              </label>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "15px 20px",
              borderTop: "1px solid #e5e5e5",
              textAlign: "right",
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: "8px 16px",
                marginRight: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={passwordChangeLoading}
              style={{
                padding: "8px 16px",
                border: "none",
                borderRadius: "4px",
                backgroundColor: "#337ab7",
                color: "#fff",
                cursor: passwordChangeLoading ? "not-allowed" : "pointer",
                opacity: passwordChangeLoading ? 0.7 : 1,
              }}
            >
              {passwordChangeLoading ? (
                <>
                  <i className="fa fa-spinner fa-spin"></i> Changing...
                </>
              ) : (
                <>
                  <i className="fa fa-save"></i> Change Password
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ChangePasswordModal;
